import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CrossIcon } from '../../../../../assets/icons';
import UseContext from '../../../../../hooks/useContext';
import ApiDatabase from '../../../../../server';
import Button from '../../../../button';
import StyledInput from '../../../../styledInput/StyledInput';
import ModalBody from '../../../components/body';
import ModalFooter from '../../../components/footer';
import JustificatifConfirmation, {
  CONFIRMATION_TITLE,
} from 'components/justificatifConfirmation/JustificatifConfirmation';
import useJustificatifConfirmation from 'hooks/useJustificatifConfirmation';
import useBaseline from 'hooks/useBaseline';
import { overtimesLabel } from 'helper/remunerationDraftChange';
import Modalheader from '../../../components/header';
import useModal from '../../../useModal';

const DRAFT_KEY = 'overtimes';

const ModalOvertimesCompany = () => {
  const { toggle } = useModal();
  const { modalData, setModalDataReturn } = UseContext();
  const [isLoading, setIsLoading] = useState(false);
  const justificatif = useJustificatifConfirmation();
  const baseline = useBaseline([]);
  // The page's changes held back (JBWEB-1468): this one joins them instead of
  // asking for its own justificatif.
  const draft = modalData?.draft;
  const staged = draft?.pending(DRAFT_KEY);
  const [isWaiting, setIsWaiting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [thresholds, setThresholds] = useState([
    {
      start: '',
      percentage: '',
    },
  ]);
  const [errors, setErrors] = useState([
    {
      start: null,
      percentage: null,
    },
  ]);
  const token = localStorage.getItem('xsrfToken');

  const addThreshold = () => {
    setThresholds((prevThresholds) => [
      ...prevThresholds,
      { start: '', percentage: '' },
    ]);
    setErrors((prevErrors) => [...prevErrors, { start: null, percentage: null }]);
  };

  const removeThreshold = (index) => {
    setThresholds((prevThresholds) =>
      prevThresholds.length > 1
        ? prevThresholds.filter((_, i) => i !== index)
        : prevThresholds
    );
    setErrors((prevErrors) =>
      prevErrors.length > 1
        ? prevErrors.filter((_, i) => i !== index)
        : prevErrors
    );
  };

  // Shared with handleChange so the inline error clears under the exact same rule
  // that gates submission.
  const validatePercentage = (value) => {
    const parsed = parseFloat(String(value).replace(',', '.'));
    if (!Number.isFinite(parsed)) return 'Saisissez un nombre.';
    if (parsed < 0 || parsed > 100) return 'La majoration doit être comprise entre 0 et 100.';
    if (Math.round(parsed * 10) / 10 !== parsed) return 'Au plus une décimale.';
    return null;
  };

  const validateThresholds = () => {
    const newErrors = thresholds.map((threshold) => {
      const errors = {
        start: !threshold.start ? 'Veuillez entrer une valeur' : null,
        percentage: validatePercentage(threshold.percentage),
      };

      return errors;
    });

    setErrors(newErrors);

    return newErrors.every((error) => !error.start && !error.percentage);
  };

  const handleSubmit = () => {
    if (!validateThresholds() || !modalData?.idCompany) return;

    // These values reach contracts and invoices, so the change carries the
    // client's written request for it before anything is written.
    // Nothing to confirm when nothing moved: the confirmation asks for a
    // justificatif, and there would be no change for it to justify.
    if (draft) {
      if (!baseline.hasChanged(thresholds)) draft.remove(DRAFT_KEY);
      else
        draft.stage({
          key: DRAFT_KEY,
          section: DRAFT_KEY,
          label: 'Heures supplémentaires',
          before: overtimesLabel(baseline.value()),
          after: overtimesLabel(thresholds),
          form: thresholds,
          preview: { type: 'overtimes_updated', overtimes: parsedThresholds() },
          commit: send,
        });
      return toggle();
    }
    if (!baseline.hasChanged(thresholds)) return;
    if (!justificatif.isConfirming) return justificatif.setIsConfirming(true);
    justificatif.confirm(save);
  };

  // The api requires percentage as an actual number (validateIncrease
  // rejects strings); the hours stay untouched.
  const parsedThresholds = () =>
    thresholds.map((threshold) => ({
      start: threshold.start,
      percentage: parseFloat(String(threshold.percentage).replace(',', '.')),
    }));

  // The save, as the modal return it resolves with: run here, or later with
  // the page's other changes.
  const send = (storedJustificatif) =>
    new Promise((resolve, reject) =>
      ApiDatabase.postOvertimesCompany(
        {
          token,
          idCompany: modalData?.idCompany,
          overtimes: parsedThresholds(),
          justificatif: storedJustificatif,
        },
        (data) => resolve({ type: 'overtimes_updated', overtimes: data.overtimes }),
        reject,
      ),
    );

  const save = (storedJustificatif) => {
    setIsLoading(true);
    send(storedJustificatif).then(
      (saved) => {
        setIsLoading(false);
        toast.success('Les heures supplémentaires ont étés mises à jour');
        setModalDataReturn(saved);
        toggle();
      },
      (err) => {
        setIsLoading(false);
        if (err.name === 'CanceledError') return;
        // The modal stays open, the input intact.
        setApiError(err?.response?.data?.message ?? "L'enregistrement a échoué");
      },
    );
  };

  const handleChange = (index, field, value) => {
    setThresholds((prevThresholds) =>
      prevThresholds.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );

    setErrors((prevErrors) =>
      prevErrors.map((error, i) => {
        if (i !== index) return error;

        const updatedError = { ...error };
        if (field === 'start') {
          updatedError[field] = !value ? 'Veuillez entrer une valeur' : null;
        }
        if (field === 'percentage') {
          updatedError.percentage = validatePercentage(value);
        }
        return updatedError;
      })
    );
    setApiError(null);
  };

  useEffect(() => {
    if (!modalData?.idCompany) return;

    setIsWaiting(true);
    ApiDatabase.getOvertimesCompany(
      { token, idCompany: modalData.idCompany },
      (data) => {
        const provThreshold = data.overtimes?.length
          ? data.overtimes.map(({ start, percentage }) => ({
              start,
              percentage: percentage === undefined || percentage === null ? '' : String(percentage),
            }))
          : [
              {
                start: '',
                percentage: '',
              },
            ];
        setThresholds(staged ?? provThreshold);
        baseline.capture(provThreshold);
        setIsWaiting(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
          setIsWaiting(false);
        }
      }
    );
  }, [modalData?.idCompany]);

  return (
    <>
      <Modalheader
        hide={toggle}
        hideShow={true}
        description={
          justificatif.isConfirming
            ? null
            : 'Les majorations que vous définissez ici s’appliquent aux heures travaillées au-delà des seuils hebdomadaires définis.'
        }
      >
        {justificatif.isConfirming ? CONFIRMATION_TITLE : 'Heures supplementaires'}
      </Modalheader>
      <ModalBody>
        {justificatif.isConfirming ? (
          <JustificatifConfirmation
            file={justificatif.file}
            setFile={justificatif.setFile}
            isAttested={justificatif.isAttested}
            setIsAttested={justificatif.setIsAttested}
            error={justificatif.error}
          />
        ) : (
          <>
        <div className="flex flex-col gap-2.5 text-sm">
          {isWaiting ? (
            <div>
              {[1, 2, 3].map((i, index) => (
                <div key={index} className="rounded-lg bg-gray-50 p-4">
                  <div className="flex flex-col gap-3">
                    <div className={'flex gap-1 justify-between'}>
                      <div className="w-56 h-4 skeleton-bar"></div>
                      <div className="w-8 h-8 skeleton-bar"></div>
                    </div>
                    <div className="flex gap-3 w-full">
                      <div className="w-full h-14 skeleton-bar"></div>
                      <div className="w-full h-14 skeleton-bar"></div>
                    </div>
                    <div>
                      <div className="w-full h-14 skeleton-bar"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {thresholds.map((threshold, index) => (
                <div key={index} className="rounded-lg bg-gray-50 p-4">
                  <div className="flex flex-col gap-3">
                    <div className={'flex gap-1 justify-between'}>
                      <div>SEUIL {index + 1}</div>
                      {thresholds.length > 1 && (
                        <div>
                          <Button
                            size={'ICON'}
                            color={'ICON'}
                            onClick={() => removeThreshold(index)}
                          >
                            <CrossIcon wh={20} color={'#374151'} />
                          </Button>
                        </div>
                      )}
                    </div>
                    <div>
                      <StyledInput
                        type="number"
                        label="À partir de"
                        value={threshold.start}
                        unit={'heures travaillées'}
                        onChange={(e) =>
                          handleChange(index, 'start', e.target.value)
                        }
                        error={errors[index]?.start}
                      />
                    </div>
                    <div>
                      <StyledInput
                        type="text"
                        label="Majoration"
                        value={threshold.percentage}
                        unit={'%'}
                        onChange={(e) =>
                          handleChange(index, 'percentage', e.target.value)
                        }
                        error={errors[index]?.percentage}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
          {apiError && <div className={'text-red-500 text-xs'}>{apiError}</div>}
        </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <div className={'w-full flex gap-1 justify-between items-center'}>
          <div>
            {!justificatif.isConfirming && (
              <Button size={'LG'} color={'SECONDARY'} onClick={addThreshold}>
                Ajouter un seuil
              </Button>
            )}
          </div>
          <div className={'flex gap-3'}>
            {isLoading || justificatif.isUploading ? (
              <>
                <Button size={'LG'} color={'SECONDARY'}>
                  Annuler
                </Button>
                <Button size={'LG'} color={'DISABLED'}>
                  Confirmation ...
                </Button>
              </>
            ) : (
              <>
                <Button
                  size={'LG'}
                  color={'SECONDARY'}
                  // Backs out of the confirmation, not the modal: the values
                  // being confirmed are still worth seeing.
                  onClick={
                    justificatif.isConfirming
                      ? () => justificatif.setIsConfirming(false)
                      : toggle
                  }
                >
                  Annuler
                </Button>
                <Button
                  size={'LG'}
                  color={
                    (!baseline.hasChanged(thresholds) && staged === undefined) ||
              (justificatif.isConfirming && !justificatif.canConfirm)
                      ? 'DISABLED'
                      : 'PRIMARY'
                  }
                  onClick={handleSubmit}
                >
                  {draft ? 'Ajouter aux modifications' : 'Confirmer'}
                </Button>
              </>
            )}
          </div>
        </div>
      </ModalFooter>
    </>
  );
};

export default ModalOvertimesCompany;
